import React from 'react';
import { CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react';

const PlatformCard = ({ platform, onSelect, onConnect, onDisconnect }) => {
  const getStatusIcon = () => {
    switch (platform?.status) {
      case 'connected':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = () => {
    const badges = {
      connected: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      warning: 'bg-yellow-100 text-yellow-800',
      disconnected: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges?.[platform?.status]}`}>
        {platform?.status?.charAt(0)?.toUpperCase() + platform?.status?.slice(1)}
      </span>
    );
  };

  const formatLastSync = () => {
    if (!platform?.lastSync) return 'Never';
    const date = new Date(platform.lastSync);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-4xl">{platform?.icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900">{platform?.name}</h3>
            {getStatusBadge()}
          </div>
        </div>
        {getStatusIcon()}
      </div>
      {/* Metrics */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Data Flow Rate:</span>
          <span className="font-medium text-gray-900">{platform?.dataFlowRate}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Last Sync:</span>
          <span className="font-medium text-gray-900">{formatLastSync()}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">API Usage:</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  platform?.apiUsage > 80 ? 'bg-red-500' :
                  platform?.apiUsage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${platform?.apiUsage}%` }}
              />
            </div>
            <span className="font-medium text-gray-900">{platform?.apiUsage}%</span>
          </div>
        </div>
      </div>
      {/* Data Types */}
      <div className="mb-4">
        <p className="text-xs text-gray-600 mb-2">Collecting:</p>
        <div className="flex flex-wrap gap-1">
          {platform?.dataTypes?.map((type, index) => (
            <span 
              key={index}
              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
            >
              {type}
            </span>
          ))}
        </div>
      </div>
      {/* Error/Warning Messages */}
      {(platform?.error || platform?.warning) && (
        <div className={`p-3 rounded-lg mb-4 ${
          platform?.error ? 'bg-red-50 text-red-800' : 'bg-yellow-50 text-yellow-800'
        }`}>
          <p className="text-xs font-medium">
            {platform?.error || platform?.warning}
          </p>
        </div>
      )}
      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onSelect}
          className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          Configure
        </button>
        
        {platform?.status === 'connected' || platform?.status === 'warning' || platform?.status === 'error' ? (
          <button
            onClick={onDisconnect}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={onConnect}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

export default PlatformCard;